import React, { useState, useMemo } from "react";
import {
  TableInfoMenu,
  StaticTable,
  Text,
  Grid,
  Icons,
  Button,
  IconButton,
  PopupPosition,
  RelativePopupWrapper,
  Menu,
} from "@pushpress/shared-components";
import { CSVLink } from "react-csv";
import {
  ChurnReportFragment,
  useGetChurnReportQuery,
} from "./ChurnReport-generated.hooks";
import Dashboard from "../../../layouts/Dashboard";
import { format } from "date-fns";
import { desktopColumns, filterDatesList, mobileColumns } from "./utils";
import { useToggle } from "../../../hooks/state";
import mock from "./mock";
import { isProd } from "../../../env";
import useIsMobile from "../../../hooks/useIsMobile";
import ChurnReportModal from "./ChurnReportModal";
import styles from "./ChurnReport.module.scss";

const ChurnReport = () => {
  const [modalData, setModalData] = useState<ChurnReportFragment | null>(null);
  const [filterDate, setFilterDate] = useState(new Date());
  const [filterVisible, filterVisibleActions] = useToggle();
  const isMobile = useIsMobile();

  const { loading, error, data } = useGetChurnReportQuery({
    variables: {
      churnInput: {
        month: Number(format(filterDate, "M")),
        year: Number(format(filterDate, "yyyy")),
      },
    },
  });

  const columns = useMemo(() => {
    return isMobile ? mobileColumns : desktopColumns;
  }, [isMobile]);

  // I have to make sure I do an Array.from because sometimes the .sort method doesn't work
  let churnReport = data?.churnReport ? Array.from(data.churnReport) : [];

  // Churn report doesn't exist on dev/stage, so if it's not prod, we just show a mock :)
  if ((error || churnReport.length === 0) && !isProd()) {
    churnReport = mock;
  }

  return (
    <div className={styles.root}>
      <Dashboard>
        <Grid container columnSpacing={0} marginY={3}>
          <Grid item xs={6}>
            <Text variant="heading-3" component="h1">
              Churn Report
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Grid container justifyContent="flex-end" alignItems="center">
              <Grid item>
                <IconButton
                  icon={Icons.Help}
                  tooltip={"Learn how we calculate churn probability"}
                  onClick={() => {
                    window.location.href = `https://help.pushpress.com/en/articles/6708070-core-churn-report`;
                  }}
                />
              </Grid>
              <Grid item>
                <CSVLink
                  data={churnReport.map((churn) => {
                    return {
                      ...churn,
                      reasons: churn.reasons
                        .map((reason) => reason.reason)
                        .join(", "),
                    };
                  })}
                  filename={"churn-report.csv"}
                  target="_blank"
                >
                  <Icons.Download color="black" />{" "}
                </CSVLink>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <ChurnReportModal
          churnReport={modalData}
          visible={!!modalData}
          onClose={() => setModalData(null)}
        />
        <StaticTable
          label="Churn report table"
          loading={loading}
          nothingToShowMessage={
            <>
              <Text variant="heading-4"> Nothing to Show</Text>
              <br />
              <Text variant="body-lg">
                Pressly did not find anyone today with a high churn probability.
                Remember, Pressly requires at least 2 months of data to
                accurately analyze churn probability. <br />
                Check back later to see if Pressly has any new insights for you.
              </Text>
            </>
          }
          customHeader={
            <RelativePopupWrapper
              popupVisible={filterVisible}
              popupComponent={
                <>
                  <Menu>
                    {filterDatesList.map((date) => (
                      <Menu.Item
                        key={String(date)}
                        label={format(date, "MMMM, yyyy")}
                        action={() => {
                          setFilterDate(date);
                          filterVisibleActions.off();
                        }}
                      />
                    ))}
                  </Menu>
                </>
              }
              popupPosition={PopupPosition.Bottom_Right_Inside}
              dismissOnOutsideClick
              setPopupVisibility={(visibility) => {
                filterVisibleActions.set(visibility);
              }}
              yOffset={10}
            >
              <Button
                onClick={filterVisibleActions.toggle}
                color="black"
                text={
                  <Text variant="heading-5" color="wolf-800">
                    {format(filterDate, "MMM, yyyy")}
                    <Icons.CaretDown className="ml-2" />
                  </Text>
                }
                buttonType="text"
              />
            </RelativePopupWrapper>
          }
          data={churnReport.sort((a, b) => {
            return (
              Number(b.churnProbability) || 0 - Number(a.churnProbability) || 0
            );
          })}
          columns={columns}
          rowsPerPage={100}
          renderKebabMenu={(data) => {
            return (
              <TableInfoMenu>
                <TableInfoMenu.Item
                  label="Info"
                  action={() => setModalData(data)}
                />
                <TableInfoMenu.Item
                  label="View Profile"
                  action={() =>
                    (window.location.href = `/members/view/${data.userUuid}`)
                  }
                />
              </TableInfoMenu>
            );
          }}
        />
      </Dashboard>
    </div>
  );
};

export default ChurnReport;
